import { z } from 'zod';

export const goalStatusSchema = z.enum(['not-started', 'in-progress', 'at-risk', 'completed', 'cancelled']);
export type GoalStatus = z.infer<typeof goalStatusSchema>;

export const goalPrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type GoalPriority = z.infer<typeof goalPrioritySchema>;

export const goalCategorySchema = z.enum([
  'performance',
  'development',
  'certification',
  'safety',
  'quality',
  'efficiency',
]);
export type GoalCategory = z.infer<typeof goalCategorySchema>;

export const goalSchema = z.object({
  goalId: z.string(),
  employeeId: z.string(),
  employeeName: z.string(),
  employeeRole: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  title: z.string(),
  description: z.string(),
  category: goalCategorySchema,
  priority: goalPrioritySchema,
  status: goalStatusSchema,
  progress: z.number().min(0).max(100),
  targetDate: z.string(),
  assignedBy: z.string(),
  assignedByName: z.string(),
  milestones: z.array(z.object({
    id: z.string(),
    title: z.string(),
    completed: z.boolean(),
    completedAt: z.string().nullable(),
  })).optional(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});

export type Goal = z.infer<typeof goalSchema>;

export const goalListResponseSchema = z.object({
  data: z.array(goalSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number(),
  }),
});

export type GoalListResponse = z.infer<typeof goalListResponseSchema>;

export interface GoalFilters {
  employeeId?: string;
  status?: GoalStatus;
  priority?: GoalPriority;
  category?: GoalCategory;
  siteId?: string;
  search?: string;
}

export function getGoalStatusColor(status: GoalStatus): string {
  const colors: Record<GoalStatus, string> = {
    'not-started': 'bg-gray-100 text-gray-700',
    'in-progress': 'bg-blue-100 text-blue-700',
    'at-risk': 'bg-red-100 text-red-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return colors[status];
}

export function getGoalPriorityColor(priority: GoalPriority): string {
  const colors: Record<GoalPriority, string> = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
  };
  return colors[priority];
}

export function getGoalCategoryLabel(category: GoalCategory): string {
  const labels: Record<GoalCategory, string> = {
    performance: 'Performance',
    development: 'Development',
    certification: 'Certification',
    safety: 'Safety',
    quality: 'Quality',
    efficiency: 'Efficiency',
  };
  return labels[category];
}
